/**
 * Late booking fee — $450 PER TEAM MEMBER (policy per Traci, 8/31/2026).
 *
 * A booking within LATE_BOOKING_FEE_DAYS of the event owes the fee once for
 * every person being staffed: the base package's team plus any add-on that
 * puts another person on the event. A photo+video package is two people, so
 * its fee is $900, not $450.
 *
 * Shared by the server charge calculation (compute.js), the client pricing
 * quote (lib/stripe.js), and express checkout, so the quote always matches
 * the charge.
 */

import { addonFamily } from '../addons/addonFamilies.js'

export const LATE_BOOKING_FEE_DAYS = 15
export const LATE_BOOKING_FEE_PER_MEMBER_CENTS = 45000

/**
 * How many people the booking staffs.
 * Base package: photo+video types = 2 (photographer + videographer), else 1.
 * Add-ons: second photographer +1, second videographer +1, and a video
 * add-on +1 only when the base package doesn't already include video
 * (on a photo+video package it's extra hours, not an extra person).
 * Each family counts at most once.
 */
export function countTeamMembers(packageType, addons = []) {
  const type = String(packageType || '').toLowerCase()
  const packageIncludesVideo = type.includes('video')
  const packageIncludesPhoto = !type.includes('videoonly') && !type.includes('video_only')
  let members = packageIncludesVideo && packageIncludesPhoto ? 2 : 1

  const countedFamilies = new Set()
  for (const addon of addons || []) {
    const family = addonFamily(addon)
    if (!family || countedFamilies.has(family)) continue
    if (family === 'second_photographer' || family === 'second_videographer') {
      countedFamilies.add(family)
      members += 1
    } else if (family === 'video_addon' && !packageIncludesVideo) {
      countedFamilies.add(family)
      members += 1
    }
  }
  return members
}

/** Late booking fee in cents for this package + add-on combination. perMemberCents: Business Controls (bookingRules). */
export function lateBookingFeeCents(packageType, addons = [], perMemberCents = LATE_BOOKING_FEE_PER_MEMBER_CENTS) {
  return perMemberCents * countTeamMembers(packageType, addons)
}

/** Same fee in whole dollars, for client-side display math. */
export function lateBookingFeeDollars(packageType, addons = [], perMemberCents = LATE_BOOKING_FEE_PER_MEMBER_CENTS) {
  return lateBookingFeeCents(packageType, addons, perMemberCents) / 100
}

/**
 * How many NEW people an amendment adds to the team (never negative —
 * removing talent does not refund a late fee).
 */
export function addedTeamMembers(packageType, existingAddons = [], addedAddons = []) {
  const before = countTeamMembers(packageType, existingAddons || [])
  const after = countTeamMembers(packageType, [...(existingAddons || []), ...(addedAddons || [])])
  return Math.max(0, after - before)
}

/**
 * Post-booking rule (Traci, 8/31/2026): adding talent within
 * LATE_BOOKING_FEE_DAYS of the event owes $450 per person added — the
 * same fee a fresh late booking would have paid for them. Past events and
 * changes outside the window owe nothing.
 */
export function amendmentLateFeeCents(packageType, existingAddons, addedAddons, daysUntilEvent, rules = {}) {
  const windowDays = rules.lateWindowDays ?? LATE_BOOKING_FEE_DAYS
  const perMemberCents = rules.lateFeePerMemberCents ?? LATE_BOOKING_FEE_PER_MEMBER_CENTS
  if (!Number.isFinite(daysUntilEvent) || daysUntilEvent < 0 || daysUntilEvent > windowDays) return 0
  return addedTeamMembers(packageType, existingAddons, addedAddons) * perMemberCents
}
